import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ONBOARDING_STEPS, useCompleteOnboarding, useOnboardingStatus } from './hooks/useOnboarding';
import { BusinessStep } from './steps/BusinessStep';
import { ChannelStep } from './steps/ChannelStep';
import { KnowledgeStep } from './steps/KnowledgeStep';
import { PlanStep } from './steps/PlanStep';

/**
 * First-run setup wizard at /onboarding/:step. New accounts (email or Google) land here.
 *
 * The current step lives in the URL path, so Meta's OAuth can send its tab straight back to the
 * channel step, and so the browser's back button moves between steps. Every step can be skipped;
 * "Skip setup", "Finish" and "Decide later" all record completion, and the dashboard checklist
 * keeps showing whatever is still open.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { step } = useParams<{ step?: string }>();
  const { data: status } = useOnboardingStatus();
  const complete = useCompleteOnboarding();

  const index = Math.max(0, ONBOARDING_STEPS.findIndex((s) => s.key === step));
  const steps = status?.steps;

  // A mistyped or stale step in the URL shows step 1; make the address bar agree.
  const knownStep = ONBOARDING_STEPS.some((s) => s.key === step);
  useEffect(() => {
    if (step !== undefined && !knownStep) navigate(`/onboarding/${ONBOARDING_STEPS[0].key}`, { replace: true });
  }, [step, knownStep, navigate]);

  const goTo = (i: number) => navigate(`/onboarding/${ONBOARDING_STEPS[i].key}`);
  // Leaves for the dashboard even if recording completion fails (the API client shows the error).
  const finish = () => complete.mutateAsync().catch(() => undefined);
  const finishAndLeave = () => {
    finish().then(() => navigate('/dashboard', { replace: true }));
  };
  const next = () => (index < ONBOARDING_STEPS.length - 1 ? goTo(index + 1) : finishAndLeave());

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-4 py-8 sm:py-12 transition-colors">
      <div className="mx-auto max-w-2xl space-y-6">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot className="h-8 w-8 text-primary" aria-hidden="true" />
            <span className="text-lg font-semibold text-slate-900 dark:text-white">Let's get you set up</span>
          </div>
          <Button variant="ghost" size="sm" onClick={finishAndLeave} disabled={complete.isPending}>
            Skip setup
          </Button>
        </header>

        <ol className="grid grid-cols-4 gap-2" aria-label="Setup progress">
          {ONBOARDING_STEPS.map((s, i) => {
            const done = steps?.[s.key] === true;
            const current = i === index;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  aria-current={current ? 'step' : undefined}
                  aria-label={`Step ${i + 1}: ${s.title}${done ? ', done' : ''}`}
                  className="w-full text-left"
                >
                  <span
                    className={`block h-1.5 rounded-full ${
                      done ? 'bg-primary' : current ? 'bg-primary/40' : 'bg-slate-200 dark:bg-slate-800'
                    }`}
                  />
                  <span
                    className={`mt-2 flex items-center gap-1 text-xs font-medium ${
                      current ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                    }`}
                  >
                    {done && <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
                    <span className="hidden sm:inline">{s.title}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <Card className="p-6">
          <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Step {index + 1} of {ONBOARDING_STEPS.length}
          </p>
          {index === 0 && <BusinessStep onDone={next} />}
          {index === 1 && (
            <ChannelStep connected={steps?.channel === true} hasBusiness={steps?.business === true} onDone={next} />
          )}
          {index === 2 && <KnowledgeStep hasBusiness={steps?.business === true} onDone={next} />}
          {index === 3 && <PlanStep finish={finish} onDone={finishAndLeave} finishing={complete.isPending} />}
        </Card>
      </div>
    </div>
  );
}
